import React, { useCallback, useContext, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	TeamsAppAnalyticsContext,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';

import { useProfileCardRequestTracking } from '../hooks/useProfileCardRequestTracking';
import { UserProfileCardContext } from '../store/context';
import type { PersonProfileData } from '../types';
import { makeGetTestId } from '../utils/testId';

import { CardError } from './components/CardError';
import { CardErrorState } from './components/CardErrorState';
import { CardLoadingState } from './components/CardLoadingState';
import { HeaderActions } from './components/HeaderActions';
import { ManagerSection } from './components/ManagerSection';
import { RecentWorkSection } from './components/RecentWorkSection';

/**
 * Props for {@link UserProfileCard} - the presentational profile card.
 */
export interface UserProfileCardProps {
	/** Profile data to render. When omitted, the card shows a loading skeleton. */
	profileData?: PersonProfileData;
	/** URLs to render as smart-link previews in the recent-work section. */
	recentWorkData?: string[];
	/** When `true`, renders the loading skeleton even if `profileData` is supplied. */
	isLoading?: boolean;
	/** Test id forwarded to the card root. */
	testId?: string;
	/** Site ID of the tenant the viewer is currently in. */
	cloudId: string;
}

const styles = cssMap({
	root: {
		width: '360px',
		minWidth: '340px',
		cursor: 'default',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('elevation.surface.raised'),
	},
});

/**
 * Presentational profile card displaying a user's core information and recent work.
 * Renders avatar, name, pronouns, job title, department, location, manager, and recent-work items.
 *
 * Shows a loading skeleton when `isLoading` is true or `profileData` is undefined.
 * Displays an error state when profile data is missing or `name` is not provided.
 *
 * Recent-work previews require a `SmartCardProvider` from `@atlaskit/link-provider`
 * in scope (already provided by every Atlassian product shell).
 */
export function UserProfileCard({
	profileData,
	recentWorkData,
	isLoading = false,
	testId = 'user-profile-card',
	cloudId,
}: UserProfileCardProps): React.JSX.Element {
	const outerContext = useContext(UserProfileCardContext);
	const userId = profileData?.accountId ?? '';
	const localGetTestId = useMemo(() => makeGetTestId(userId, testId), [userId, testId]);
	// Prefer the outer `getTestId` when this card is rendered inside `UserProfileCardWithTrigger`
	// so the trigger and card agree on the same testId base. Only fall back to a fresh factory
	// when there is no outer context (i.e. the card is being used standalone).
	const getTestId = outerContext.isProvided ? outerContext.getTestId : localGetTestId;
	// Forwards `townsquareApp` / `onProfileClick` from any outer provider (e.g. when
	// rendered via `UserProfileCardWithTrigger`) so standalone consumers of
	// `UserProfileCard` and trigger-wrapped consumers see the same context shape.
	const contextValue = useMemo(
		() => ({
			cloudId,
			getTestId,
			townsquareApp: outerContext.townsquareApp,
			onProfileClick: outerContext.onProfileClick,
			isProvided: true as const,
		}),
		[cloudId, getTestId, outerContext.townsquareApp, outerContext.onProfileClick],
	);

	return (
		<TeamsAppAnalyticsContext
			data={{
				source: 'userProfileCard',
				attributes: {
					cloudId,
					viewedUserId: userId,
					hasManager: Boolean(profileData?.manager),
					hasRecentWork: Boolean(recentWorkData?.length),
				},
			}}
		>
			<UserProfileCardContext.Provider value={contextValue}>
				<UserProfileCardErrorBoundary testId={testId}>
					<UserProfileCardContent
						profileData={profileData}
						recentWorkData={recentWorkData}
						isLoading={isLoading}
						testId={testId}
						cloudId={cloudId}
					/>
				</UserProfileCardErrorBoundary>
			</UserProfileCardContext.Provider>
		</TeamsAppAnalyticsContext>
	);
}

function UserProfileCardErrorBoundary({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId: string;
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();

	const handleError = useCallback(() => {
		fireEvent('ui.userProfileCard.rendered.errorBoundary', {});
	}, [fireEvent]);

	return (
		<ErrorBoundary onError={handleError} fallback={<CardError testId={testId} />}>
			{children}
		</ErrorBoundary>
	);
}

function UserProfileCardContent({
	profileData,
	recentWorkData,
	isLoading,
	testId,
	cloudId,
}: Required<Pick<UserProfileCardProps, 'isLoading' | 'testId' | 'cloudId'>> &
	Pick<UserProfileCardProps, 'profileData' | 'recentWorkData'>): React.JSX.Element {
	const { getTestId } = useContext(UserProfileCardContext);
	const hasProfileData = Boolean(profileData && profileData.name);

	useProfileCardRequestTracking(isLoading, hasProfileData);

	if (isLoading) {
		return <CardLoadingState testId={testId} />;
	}

	if (!profileData || !profileData.name) {
		return <CardErrorState testId={testId} errorType="NotFound" />;
	}
	const { name, pronouns, avatarUrl, jobTitle, department, location, localTime, manager } =
		profileData;

	const subtitleParts = [jobTitle, department].filter(Boolean);
	const subtitle = subtitleParts.join(` \u2022 `);

	const locationLine =
		localTime && location ? `${location} \u2013 ${localTime}` : (location ?? undefined);

	const hasRecentWork = Boolean(recentWorkData?.length);

	return (
		<Box xcss={styles.root} testId={testId} role="region" aria-label={name}>
			<Stack space="space.200">
				<Stack space="space.100">
					<Inline spread="space-between" alignBlock="start">
						<Avatar
							src={avatarUrl ?? undefined}
							name={name}
							size="xlarge"
							testId={getTestId('avatar')}
						/>
						<HeaderActions profileData={profileData} cloudId={cloudId} />
					</Inline>

					<Stack space="space.075">
						<Inline space="space.100" alignBlock="end">
							<Heading as="h2" size="medium" testId={getTestId('name')}>
								{name}
							</Heading>
							{pronouns && (
								<Text size="small" color="color.text.subtlest" testId={getTestId('pronouns')}>
									{pronouns}
								</Text>
							)}
						</Inline>
						{subtitle && (
							<Text maxLines={2} color="color.text.subtlest" testId={getTestId('subtitle')}>
								{subtitle}
							</Text>
						)}
						{locationLine && (
							<Text
								maxLines={2}
								size="small"
								color="color.text.subtlest"
								testId={getTestId('location')}
							>
								{locationLine}
							</Text>
						)}
					</Stack>
				</Stack>

				{manager && <ManagerSection manager={manager} testId={getTestId('manager')} />}

				{hasRecentWork && recentWorkData && (
					<RecentWorkSection recentWorkData={recentWorkData} testId={getTestId('recent-work')} />
				)}
			</Stack>
		</Box>
	);
}
